import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { BedReqIncData, BedRequestMock } from '../types/bedRequest.type'
import { GeneralResMock } from '../types/general.type'

type Props = Partial<{
  page: number
  limit: number
  query?: string
  sortDir?: 'asc' | 'desc'
  bedId?: string
  patientId?: string
  status?: 'pending' | 'approved' | 'rejected'
  requestedBy?: string
  requestedTo?: string
  requestedAt?: string
  approvedBy?: string
  approvedAt?: string
  rejectedBy?: string
  rejectedAt?: string
  bedStatus?: 'available' | 'occupied' | 'reserved'
  default?: boolean
}>
export const useBedRequest = (arg: Props) => {
  const page = arg?.page || 1
  const limit = arg?.limit || 10
  const query = arg?.query
  const status = arg?.status

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      let endpoint = 'bed_requests?'
      if (page) endpoint += `page%5Bnumber%5D=${page}&page%5Bsize%5D=${limit}&`
      if (query) endpoint += `query=${query}&`
      if (arg.bedId) endpoint += `bed_id=${arg.bedId}&`
      if (arg.patientId) endpoint += `patient_id=${arg.patientId}&`
      if (status) endpoint += `status=${status}&`
      if (arg.requestedBy) endpoint += `requested_by=${arg.requestedBy}&`
      if (arg.requestedTo) endpoint += `requested_to=${arg.requestedTo}&`
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<BedRequestMock[], BedReqIncData[]>>(endpoint)
      return data
    },
    queryKey: ['bedRequest', { limit, page, query, status, bedId: arg.bedId, patientId: arg.patientId }]
  })

  const { data: singleBedRequest, isLoading: singleReqLoading } = useQuery({
    enabled: !!arg.bedId,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<BedRequestMock, BedReqIncData[]>>(
        `bed_requests/${arg.bedId}`
      )
      return data
    },
    queryKey: ['bedRequest', arg.bedId]
  })

  const { mutateAsync: updateRequest } = useMutation({
    mutationFn: async ({ payload, id }: { payload: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`bed_requests/${id}/assign_bed`, payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bedRequest'] })
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    }
  })

  return {
    bedRequests: data?.data,
    isLoading,
    pagination: data?.meta.pagination,
    requestIncluded: data?.included,
    singleBedRequest,
    singleReqLoading,
    updateRequest
  }
}
